import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { BuyNowStatus } from '@/types/item/ItemSummary';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getItemSummary } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { ItemCardComponent, ItemCardInfo } from '@/components/GridItemCard/types/types';
import { useAppSelector } from '@/redux/hooks';
import ItemCardAuctionName from '@/components/GridItemCard/Card/components/ItemCardAuctionName/ItemCardAuctionName';
import ItemCardBadge, {
    StyledFeaturedBadge,
} from '@/components/GridItemCard/Card/components/ItemCardBadge/ItemCardBadge';
import ItemCardImage from '@/components/GridItemCard/Card/components/ItemCardImage/ItemCardImage';
import ItemCardMenu from '@/components/ItemCard/components/Menu/ItemCardMenu';
import ItemCardPriceRow from '@/components/GridItemCard/Card/components/ItemCardPriceRow/ItemCardPriceRow';
import ItemCardSaveIcon from '@/components/GridItemCard/Card/components/ItemCardSaveIcon/ItemCardSaveIcon';
import ItemCardShortDate from '@/components/GridItemCard/Card/components/ItemCardShortDate/ItemCardShortDate';
import ItemCardTitle from '@/components/GridItemCard/Card/components/ItemCardTitle/ItemCardTitle';
import ms from 'ms';
import React, { useState } from 'react';
import styled from 'styled-components';
import theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';

const UpcomingSalesCard: ItemCardComponent = ({ className, itemId }) => {
    const isLoggedIn = useAppSelector(isUserLoggedIn);
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));

    const { bidCount, buyNowStatus, catalogStatus, isLocked, isPassed, isSold, saleStartTs } = useAppSelector((state) =>
        getItemSummary(state, itemId)
    );
    const [hover, setHover] = useState(false);
    const [linkHover, setLinkHover] = useState(false);
    const biddingWithin7Days = Date.now() > saleStartTs * 1000 - ms('7d');
    const showDateWithoutYear = catalogStatus === 'online' && !biddingWithin7Days;

    const isBuyNow = buyNowStatus === BuyNowStatus.Available;

    const enterHoverState = React.useCallback(() => {
        setHover(true);
    }, []);

    const leaveHoverState = React.useCallback(() => {
        setHover(false);
    }, []);

    const enterLinkHoverState = React.useCallback(() => {
        setLinkHover(true);
    }, []);

    const leaveLinkHoverState = React.useCallback(() => {
        setLinkHover(false);
    }, []);

    const isPending = isLocked && !isPassed && !isSold && !isLotOpen;
    const auctionDateInfo = isBuyNow ? (
        <BodySecondary color="secondary">
            <FormattedMessage id="buy_now" />
        </BodySecondary>
    ) : (
        <ItemCardShortDate
            allowBidding
            itemId={itemId}
            showDateWithoutYear={showDateWithoutYear}
            showOnlyYear={isSold || isPassed || isPending}
        />
    );
    return (
        <StyledCard
            data-testid={`upcoming-sales-price-results-card-${itemId}`}
            onMouseEnter={enterHoverState}
            onMouseLeave={leaveHoverState}
        >
            <UpcomingSalesPriceResultsCardGrid
                className={className}
                data-testid="upcoming-sales-price-results-card-grid"
            >
                <StyledImageContainer
                    data-testid="image-container"
                    onMouseEnter={enterLinkHoverState}
                    onMouseLeave={leaveLinkHoverState}
                >
                    <StyledItemCardBadge
                        itemId={itemId}
                        savedItemsCard
                    />
                    <StyledFavoriteButton>
                        <ItemCardSaveIcon itemId={itemId} />
                    </StyledFavoriteButton>
                    <PlacedItemCardImage itemId={itemId} />
                </StyledImageContainer>
                <PlacedDetailsForNewMobileGrid>
                    <PlacedItemInfo>
                        <StyledDateInfo>{auctionDateInfo}</StyledDateInfo>
                        <ItemCardAuctionName itemId={itemId} />
                    </PlacedItemInfo>
                    <PlacedItemCardTitle
                        isHovered={linkHover}
                        itemId={itemId}
                        onMouseEnter={enterLinkHoverState}
                        onMouseLeave={leaveLinkHoverState}
                        testid="itemCardTitle"
                    />
                    <PlacedPriceInformation>
                        <StyledBidInfo>
                            <ItemCardPriceRow
                                isHovered={linkHover}
                                itemId={itemId}
                                onMouseEnter={enterLinkHoverState}
                                onMouseLeave={leaveLinkHoverState}
                                showBidCount={Boolean(bidCount)}
                            />
                        </StyledBidInfo>
                        {isLoggedIn && (
                            <ItemCardMenu
                                itemId={itemId}
                                showIcon={hover}
                            />
                        )}
                    </PlacedPriceInformation>
                </PlacedDetailsForNewMobileGrid>
            </UpcomingSalesPriceResultsCardGrid>
        </StyledCard>
    );
};

const info: ItemCardInfo = {
    dimensions: { desktop: 282, mobile: 163 },
    ItemCardComponent: UpcomingSalesCard,
};

export default info;

const StyledCard = styled.article`
    position: relative;
`;

const StyledItemCardBadge = styled(ItemCardBadge)`
    ${StyledFeaturedBadge} {
        top: 8px;
        left: 8px;
    }
`;

const PlacedItemCardImage = styled(ItemCardImage)`
    grid-area: itemimage;
`;

const PlacedDetailsForNewMobileGrid = styled.div`
    display: grid;
    overflow: hidden;
    grid-area: alldetailsformobilelayout;
    grid-template-areas: '.' 'iteminfo' 'title' '.' 'currentbidprice';
    grid-template-rows: 6px 22px min-content 4px;

    @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
        grid-template-rows: min-content 2px min-content 2px;
        grid-template-areas: 'iteminfo' '.' 'title' '.' 'currentbidprice';
    }
`;

const StyledFavoriteButton = styled.span`
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
`;

const PlacedItemInfo = styled.div`
    grid-area: iteminfo;
    display: flex;
    justify-content: space-between;
    min-width: 0;
`;

const StyledDateInfo = styled.span`
    flex-grow: 1;
    line-height: 18px;
`;

const PlacedItemCardTitle = styled(ItemCardTitle)`
    grid-area: title;
`;

const StyledBidInfo = styled.div`
    display: flex;
    align-items: center;
`;

const PlacedPriceInformation = styled.div`
    grid-area: currentbidprice;
    display: flex;
    justify-content: space-between;
`;

const StyledImageContainer = styled.div`
    position: relative;
    aspect-ratio: 1/1;
`;

const UpcomingSalesPriceResultsCardGrid = styled.section`
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: min-content min-content min-content;
    grid-template-areas:
        'itemimage'
        '.'
        'alldetailsformobilelayout';
`;
